import { useMemo } from 'react'

import {
  CalendarCheck,
  CircleDot,
  ExternalLink,
  MountainSnow,
  Pencil,
  Play,
  TrendingUp,
} from 'lucide-react'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { LabelValue } from '~/components/component-library/LabelValue'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import {
  SegmentedAssetsBar,
  Segment,
} from '~/components/component-library/SegmentedAssetsBar'
import { Timestamp } from '~/components/component-library/Timestamp'
import { TokenBalance } from '~/components/component-library/TokenBalance'
import { ClaimDialog } from '~/components/smart-accounts/vesting/ClaimDialog'
import { getProtocolLabel } from '~/components/smart-accounts/vesting/VestingBalances'
import { useVestingDetails } from '~/components/smart-accounts/vesting/hooks/useVestingDetails'
import { VestingBalance } from '~/components/smart-accounts/vesting/types'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org/useActiveOrg'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'

export const VestingDetailsDialog = ({
  vestingBalance,
}: {
  vestingBalance: VestingBalance
}) => {
  const { data: details, isLoading } = useVestingDetails(vestingBalance.id)
  const { setGlobalModalState } = useAuth()
  const { org } = useActiveOrg()

  const isInactiveChain = !(
    org?.smartAccountsChainIds.includes(vestingBalance.chainId) ?? false
  )

  const tokenMetadata = useTokenMetadata({
    address: vestingBalance.tokenAddress,
    chainId: vestingBalance.chainId,
    includePrice: false,
  })

  const title = tokenMetadata?.symbol
    ? `Vesting Plan: ${tokenMetadata.symbol}`
    : 'Vesting Plan'

  const handleClaimClick = () => {
    setGlobalModalState({
      isOpen: true,
      content: (
        <ClaimDialog
          vestingBalance={vestingBalance}
          setIsOpen={() => {
            setGlobalModalState({
              isOpen: false,
              content: null,
            })
          }}
        />
      ),
    })
  }

  const cliffNotReached = useMemo(
    () =>
      vestingBalance.cliffTime !== undefined &&
      new Date().getTime() < vestingBalance.cliffTime * 1000,
    [vestingBalance.cliffTime],
  )

  const hasClaimableAmount = vestingBalance.claimableAmount > 0

  return (
    <SplitsDialogContent
      id="vestingDetails"
      title={title}
      variant="cta"
      actionButton={{
        text: 'Claim',
        onClick: handleClaimClick,
        isDisabled: isInactiveChain || cliffNotReached || !hasClaimableAmount,
        disabledReason: cliffNotReached
          ? 'Cliff not reached'
          : isInactiveChain
            ? 'Chain is inactive'
            : !hasClaimableAmount
              ? 'No claimable amount'
              : undefined,
      }}
      cancelButton={{
        text: `View on ${getProtocolLabel(vestingBalance.protocol)}`,
        href: {
          path: vestingBalance.streamURL,
          target: 'tab',
        },
        prefix: <ExternalLink className="h-4 w-4" />,
      }}
    >
      {isLoading || !details ? (
        <div className="flex min-h-48 items-center justify-center">
          <LoadingSpinner size="xl" thickness="normal" variant="default" />
        </div>
      ) : (
        <VestingDetailsContent
          vestingBalance={vestingBalance}
          totalAmount={details.totalAmount}
          vestedAmount={details.vestedAmount}
          claimedAmount={details.claimedAmount}
          startTime={details.startTime}
          endTime={details.endTime}
          cliffTime={details.cliffTime}
          shape={details.shape}
          isCancelable={details.isCancelable}
        />
      )}
    </SplitsDialogContent>
  )
}

const VestingDetailsContent = ({
  vestingBalance,
  totalAmount,
  vestedAmount,
  claimedAmount,
  startTime,
  endTime,
  cliffTime,
  shape,
  isCancelable,
}: {
  vestingBalance: VestingBalance
  totalAmount: bigint
  vestedAmount: bigint
  claimedAmount: bigint
  startTime: number
  endTime: number
  cliffTime: number | undefined
  shape: string
  isCancelable: boolean
}) => {
  const { chainId, tokenAddress } = vestingBalance
  const { convertTokenToUSD } = useTokenPrices()

  const tokenMetadata = useTokenMetadata({
    address: tokenAddress,
    chainId,
    includePrice: true,
  })

  // Calculate USD values for segments
  const totalUsdValue =
    convertTokenToUSD({ token: tokenAddress, amount: totalAmount, chainId }) ??
    0
  const claimedUsdValue =
    convertTokenToUSD({
      token: tokenAddress,
      amount: claimedAmount,
      chainId,
    }) ?? 0

  // Calculate segment values
  const availableToClaimAmount = vestedAmount - claimedAmount
  const notVestedAmount = totalAmount - vestedAmount

  const availableToClaimUsdValue =
    convertTokenToUSD({
      token: tokenAddress,
      amount: availableToClaimAmount,
      chainId,
    }) ?? 0
  const notVestedUsdValue =
    convertTokenToUSD({
      token: tokenAddress,
      amount: notVestedAmount,
      chainId,
    }) ?? 0
  const hasCliff = cliffTime != null && cliffTime > 0

  // Always show all three segments (Claimed, Vested, Unvested)
  const segments: Segment[] = useMemo(() => {
    return [
      {
        name: 'Claimed',
        color: '#22c55e', // green-500
        value: claimedUsdValue,
        items: [
          {
            symbol: tokenMetadata?.symbol ?? '',
            address: tokenAddress,
            chainId,
            amount: claimedAmount,
            value: claimedUsdValue,
          },
        ],
      },
      {
        name: 'Vested',
        color: '#3b82f6', // blue-500
        value: availableToClaimUsdValue,
        items: [
          {
            symbol: tokenMetadata?.symbol ?? '',
            address: tokenAddress,
            chainId,
            amount: availableToClaimAmount,
            value: availableToClaimUsdValue,
          },
        ],
      },
      {
        name: 'Unvested',
        color: '#9ca3af', // gray-400
        value: notVestedUsdValue,
        items: [
          {
            symbol: tokenMetadata?.symbol ?? '',
            address: tokenAddress,
            chainId,
            amount: notVestedAmount,
            value: notVestedUsdValue,
          },
        ],
      },
    ]
  }, [
    claimedAmount,
    claimedUsdValue,
    availableToClaimAmount,
    availableToClaimUsdValue,
    notVestedAmount,
    notVestedUsdValue,
    tokenMetadata?.symbol,
    tokenAddress,
    chainId,
  ])

  return (
    <div className="space-y-4">
      <SegmentedAssetsBar
        totalValue={totalUsdValue}
        segments={segments}
        showAllSegments
        simplifiedTooltip
      />

      <div className="space-y-0">
        <LabelValue
          icon={<CircleDot />}
          label="Total"
          listIndex={0}
          size="small"
          value={
            <TokenBalance
              address={tokenAddress}
              chainId={chainId}
              tokenAmount={totalAmount}
              layout="standard"
              size="small"
              direction="neutral"
              showNetworkIcon
            />
          }
        />

        <LabelValue
          icon={<Play />}
          label="Vesting start"
          listIndex={1}
          size="small"
          value={<Timestamp timestamp={startTime * 1000} type="absolute" />}
        />

        <LabelValue
          icon={<CalendarCheck />}
          label="Vesting end"
          listIndex={2}
          size="small"
          value={<Timestamp timestamp={endTime * 1000} type="absolute" />}
        />

        <LabelValue
          icon={<TrendingUp />}
          label="Shape"
          listIndex={3}
          size="small"
          value={shape}
        />

        {hasCliff && (
          <LabelValue
            icon={<MountainSnow />}
            label="Cliff"
            listIndex={4}
            size="small"
            value={<Timestamp timestamp={cliffTime * 1000} type="absolute" />}
          />
        )}

        <LabelValue
          icon={<Pencil />}
          label="Immutable"
          listIndex={hasCliff ? 5 : 4}
          size="small"
          value={isCancelable ? 'No' : 'Yes'}
        />
      </div>
    </div>
  )
}
